import { motion, AnimatePresence } from "framer-motion";
import { Shield, X, ExternalLink, MessageCircle, CheckCircle } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { BRAND } from "../lib/brandConfig";

const CLPTourInterceptModal = ({ open, onClose, leadId, externalUrl }) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleProceed = async () => {
    setAcknowledged(true);
    if (leadId) {
      try {
        await axios.post(`${API}/clp/track`, {
          lead_id: leadId,
          event_type: "acknowledge_clp",
        });
      } catch (e) {
        console.error("CLP ack failed:", e);
      }
    }
    // Brief delay then open the external URL
    setTimeout(() => {
      if (externalUrl) {
        window.open(externalUrl, "_blank", "noopener,noreferrer");
      }
      onClose();
      setAcknowledged(false);
    }, 800);
  };

  const handleSkip = () => {
    if (externalUrl) {
      window.open(externalUrl, "_blank", "noopener,noreferrer");
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            data-testid="clp-tour-intercept-modal"
          >
            <div className="bg-[#DE6E4B] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-white" />
                <h3 className="text-white font-semibold text-base">Before You Go</h3>
              </div>
              <button
                onClick={onClose}
                className="text-white/60 hover:text-white transition-colors"
                data-testid="clp-tour-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {acknowledged ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-4"
                >
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-[#0A2342] font-semibold text-sm">Opening listing...</p>
                </motion.div>
              ) : (
                <>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    Ready to schedule a tour? Awesome! Just one quick thing:
                  </p>
                  <div className="bg-[#DE6E4B]/10 rounded-xl p-4 mb-5 border border-[#DE6E4B]/20">
                    <p className="text-[#0A2342] text-sm font-medium leading-relaxed">
                      When you visit or apply, please write{" "}
                      <strong>"{BRAND.agentAttribution}"</strong> on the guest
                      card. This ensures I can keep helping you for{" "}
                      <strong>free</strong>.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={handleProceed}
                      className="w-full py-3 bg-[#DE6E4B] text-white rounded-xl font-medium hover:bg-[#c5593d] transition-colors text-sm flex items-center justify-center gap-2"
                      data-testid="clp-tour-proceed-btn"
                    >
                      <ExternalLink className="w-4 h-4" /> I'll list Bryan &mdash; Continue
                    </button>
                    <button
                      onClick={handleSkip}
                      className="w-full py-2.5 text-slate-400 text-xs hover:text-slate-600 transition-colors"
                      data-testid="clp-tour-skip-btn"
                    >
                      Skip for now
                    </button>
                    <a
                      href="sms:+19362241484?body=Hi%20Bryan%2C%20I%27m%20about%20to%20tour%20a%20property!"
                      className="w-full py-2.5 border border-slate-200 text-slate-600 rounded-xl font-medium hover:bg-slate-50 transition-colors text-xs text-center flex items-center justify-center gap-2"
                      data-testid="clp-tour-text-btn"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> Let Bryan know you're touring
                    </a>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CLPTourInterceptModal;
